import { Link } from 'react-router-dom';
import { ROUTES } from '@traction/shared';
import { Check, X, ArrowRight, Zap, Clock, DollarSign, Users, Sparkles } from 'lucide-react';

type ComparisonValue = boolean | string;

interface ComparisonRow {
  feature: string;
  tractionBee: ComparisonValue;
  apollo: ComparisonValue;
  clay: ComparisonValue;
  category: 'discovery' | 'outreach' | 'integration' | 'pricing';
}

const comparisonData: ComparisonRow[] = [
  // Lead Discovery
  { feature: 'AI lead discovery', tractionBee: true, apollo: false, clay: false, category: 'discovery' },
  { feature: 'Buying signal detection', tractionBee: true, apollo: 'Limited', clay: 'Manual setup', category: 'discovery' },
  { feature: 'ICP-based filtering', tractionBee: true, apollo: true, clay: true, category: 'discovery' },
  { feature: 'Shared connections auto-detected', tractionBee: true, apollo: false, clay: false, category: 'discovery' },
  { feature: 'Daily curated leads', tractionBee: '3-25/day', apollo: 'BYO list', clay: 'BYO list', category: 'discovery' },

  // Email & Outreach
  { feature: 'AI-written personalized emails', tractionBee: true, apollo: false, clay: 'Templates', category: 'outreach' },
  { feature: 'Learns your writing style', tractionBee: true, apollo: false, clay: false, category: 'outreach' },
  { feature: 'Common ground in emails', tractionBee: true, apollo: false, clay: 'Manual', category: 'outreach' },
  { feature: 'Reply intent detection', tractionBee: true, apollo: 'Add-on', clay: false, category: 'outreach' },
  { feature: 'Follow-up suggestions', tractionBee: true, apollo: false, clay: false, category: 'outreach' },

  // Integration
  { feature: 'Gmail Chrome extension', tractionBee: true, apollo: true, clay: false, category: 'integration' },
  { feature: 'One-click send from Gmail', tractionBee: true, apollo: false, clay: false, category: 'integration' },
  { feature: 'CSV import', tractionBee: true, apollo: true, clay: true, category: 'integration' },
  { feature: 'Setup time', tractionBee: '5 min', apollo: '30+ min', clay: '2-8 hours', category: 'integration' },

  // Pricing & Support
  { feature: 'Starting price', tractionBee: '$49/mo', apollo: '$59/mo', clay: '$149/mo', category: 'pricing' },
  { feature: 'Free trial', tractionBee: '14 days', apollo: 'Limited', clay: 'None', category: 'pricing' },
  { feature: 'No credit card for trial', tractionBee: true, apollo: false, clay: false, category: 'pricing' },
  { feature: 'Built for founders', tractionBee: true, apollo: 'Enterprise focus', clay: 'Enterprise focus', category: 'pricing' },
];

function ComparisonCell({ value }: { value: ComparisonValue }) {
  if (typeof value === 'boolean') {
    return value ? (
      <Check className="h-5 w-5 text-green-400 mx-auto" />
    ) : (
      <X className="h-5 w-5 text-red-400/50 mx-auto" />
    );
  }
  return <span className="text-sm text-cacao-muted">{value}</span>;
}

export function ComparePage() {
  const categories = [
    { id: 'discovery', label: 'Lead Discovery', icon: Zap },
    { id: 'outreach', label: 'Email & Outreach', icon: Sparkles },
    { id: 'integration', label: 'Integration', icon: Users },
    { id: 'pricing', label: 'Pricing & Value', icon: DollarSign },
  ];

  return (
    <div className="bg-cacao-dark">
      {/* Hero Section */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#F5F5F5]">
            TractionBee vs Apollo vs Clay
          </h1>
          <p className="mt-4 text-lg text-cacao-muted max-w-2xl mx-auto">
            See how TractionBee compares to Apollo.io and Clay for founder-led outreach.
            <span className="text-[#F5F5F5]"> Spoiler: we're built different.</span>
          </p>
        </div>
      </section>

      {/* Quick Differentiators */}
      <section className="border-t border-cacao-dark-tertiary py-12 bg-cacao-dark-secondary/50">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            <div className="rounded-xl border border-cacao-gold/30 bg-cacao-gold/5 p-6 text-center">
              <Clock className="h-8 w-8 text-cacao-gold mx-auto" />
              <h3 className="mt-3 text-lg font-semibold text-[#F5F5F5]">2 minutes vs 2 hours</h3>
              <p className="mt-2 text-sm text-cacao-muted">
                Apollo and Clay require manual research. TractionBee delivers ready-to-send emails.
              </p>
            </div>
            <div className="rounded-xl border border-cacao-gold/30 bg-cacao-gold/5 p-6 text-center">
              <Sparkles className="h-8 w-8 text-cacao-gold mx-auto" />
              <h3 className="mt-3 text-lg font-semibold text-[#F5F5F5]">AI writes, you send</h3>
              <p className="mt-2 text-sm text-cacao-muted">
                Others give you data. We give you personalized emails ready to go.
              </p>
            </div>
            <div className="rounded-xl border border-cacao-gold/30 bg-cacao-gold/5 p-6 text-center">
              <DollarSign className="h-8 w-8 text-cacao-gold mx-auto" />
              <h3 className="mt-3 text-lg font-semibold text-[#F5F5F5]">Founder pricing</h3>
              <p className="mt-2 text-sm text-cacao-muted">
                No enterprise sales calls. Start at $49/mo with a real free trial.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Comparison Table */}
      <section className="border-t border-cacao-dark-tertiary py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-2xl font-bold text-[#F5F5F5] mb-10">
            Feature-by-Feature Comparison
          </h2>

          <div className="overflow-x-auto rounded-xl border border-cacao-dark-tertiary">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="bg-cacao-dark-tertiary">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-cacao-muted w-2/5">Feature</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-cacao-gold w-1/5">TractionBee</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-cacao-muted w-1/5">Apollo.io</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-cacao-muted w-1/5">Clay</th>
                </tr>
              </thead>
              <tbody className="bg-cacao-dark-secondary">
                {categories.map((category) => (
                  <>
                    <tr key={category.id} className="border-t border-cacao-dark-tertiary bg-cacao-dark">
                      <td colSpan={4} className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <category.icon className="h-4 w-4 text-cacao-gold" />
                          <span className="text-xs font-semibold text-cacao-gold uppercase tracking-wide">
                            {category.label}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {comparisonData
                      .filter((row) => row.category === category.id)
                      .map((row, idx) => (
                        <tr
                          key={row.feature}
                          className={idx % 2 === 0 ? 'bg-cacao-dark-secondary' : 'bg-cacao-dark-secondary/50'}
                        >
                          <td className="px-6 py-3 text-sm text-[#F5F5F5]">{row.feature}</td>
                          <td className="px-6 py-3 text-center">
                            <ComparisonCell value={row.tractionBee} />
                          </td>
                          <td className="px-6 py-3 text-center">
                            <ComparisonCell value={row.apollo} />
                          </td>
                          <td className="px-6 py-3 text-center">
                            <ComparisonCell value={row.clay} />
                          </td>
                        </tr>
                      ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* vs Apollo Section */}
      <section className="border-t border-cacao-dark-tertiary py-16 bg-cacao-dark-secondary/30">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#F5F5F5] mb-6">TractionBee vs Apollo.io</h2>
          <div className="prose prose-invert max-w-none">
            <p className="text-cacao-muted leading-relaxed">
              <span className="text-[#F5F5F5] font-medium">Apollo.io is a powerful B2B database</span> with
              275M+ contacts. It's great for enterprise sales teams who need data enrichment and have
              SDRs to do the manual outreach work.
            </p>
            <p className="text-cacao-muted leading-relaxed mt-4">
              <span className="text-[#F5F5F5] font-medium">The problem for founders:</span> Apollo gives
              you the data, but you still have to research each lead, write personalized emails, and
              manage the outreach yourself. That's 2+ hours of work daily.
            </p>
            <p className="text-cacao-muted leading-relaxed mt-4">
              <span className="text-[#F5F5F5] font-medium">TractionBee does the work for you.</span> We
              use Apollo's data under the hood, but our AI curates leads showing buying signals, writes
              hyper-personalized emails, and delivers them ready to send in Gmail. You spend 2 minutes
              instead of 2 hours.
            </p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark p-5">
              <p className="text-xs font-semibold text-cacao-muted uppercase tracking-wide mb-3">Choose Apollo if...</p>
              <ul className="space-y-2 text-sm text-cacao-muted">
                <li className="flex items-start gap-2">
                  <span className="text-cacao-muted mt-0.5">•</span>
                  You have an SDR team to do outreach
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cacao-muted mt-0.5">•</span>
                  You need raw data for CRM enrichment
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cacao-muted mt-0.5">•</span>
                  You have 2+ hours daily for prospecting
                </li>
              </ul>
            </div>
            <div className="rounded-lg border border-cacao-gold/30 bg-cacao-gold/5 p-5">
              <p className="text-xs font-semibold text-cacao-gold uppercase tracking-wide mb-3">Choose TractionBee if...</p>
              <ul className="space-y-2 text-sm text-cacao-muted">
                <li className="flex items-start gap-2">
                  <span className="text-cacao-gold mt-0.5">•</span>
                  You're a founder doing your own outreach
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cacao-gold mt-0.5">•</span>
                  You want ready-to-send personalized emails
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cacao-gold mt-0.5">•</span>
                  You have 2 minutes, not 2 hours
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* vs Clay Section */}
      <section className="border-t border-cacao-dark-tertiary py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#F5F5F5] mb-6">TractionBee vs Clay</h2>
          <div className="prose prose-invert max-w-none">
            <p className="text-cacao-muted leading-relaxed">
              <span className="text-[#F5F5F5] font-medium">Clay is a powerful data enrichment platform</span> that
              lets you build custom workflows with 75+ data providers. It's incredibly flexible for
              teams with technical resources to set up complex automations.
            </p>
            <p className="text-cacao-muted leading-relaxed mt-4">
              <span className="text-[#F5F5F5] font-medium">The problem for founders:</span> Clay has a
              steep learning curve (expect 2-8 hours to set up). You need to bring your own lead lists,
              configure enrichment waterfall, and still write your own emails. It's built for ops teams,
              not busy founders.
            </p>
            <p className="text-cacao-muted leading-relaxed mt-4">
              <span className="text-[#F5F5F5] font-medium">TractionBee is turnkey.</span> Sign up, define
              your ICP, and start receiving curated leads with personalized emails tomorrow morning. No
              complex setup, no workflow building, no technical configuration required.
            </p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark p-5">
              <p className="text-xs font-semibold text-cacao-muted uppercase tracking-wide mb-3">Choose Clay if...</p>
              <ul className="space-y-2 text-sm text-cacao-muted">
                <li className="flex items-start gap-2">
                  <span className="text-cacao-muted mt-0.5">•</span>
                  You have a RevOps team to build workflows
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cacao-muted mt-0.5">•</span>
                  You need complex multi-source enrichment
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cacao-muted mt-0.5">•</span>
                  You have hours to invest in setup
                </li>
              </ul>
            </div>
            <div className="rounded-lg border border-cacao-gold/30 bg-cacao-gold/5 p-5">
              <p className="text-xs font-semibold text-cacao-gold uppercase tracking-wide mb-3">Choose TractionBee if...</p>
              <ul className="space-y-2 text-sm text-cacao-muted">
                <li className="flex items-start gap-2">
                  <span className="text-cacao-gold mt-0.5">•</span>
                  You want it working in 5 minutes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cacao-gold mt-0.5">•</span>
                  You don't have a technical ops team
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cacao-gold mt-0.5">•</span>
                  You want leads delivered, not DIY
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Line Section */}
      <section className="border-t border-cacao-dark-tertiary py-16 bg-cacao-dark-secondary/30">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#F5F5F5] text-center mb-8">The Bottom Line</h2>
          <div className="rounded-xl border border-cacao-gold/30 bg-cacao-gold/5 p-8">
            <p className="text-lg text-[#F5F5F5] text-center leading-relaxed">
              Apollo and Clay are powerful tools for <span className="text-cacao-gold">sales teams with dedicated resources</span>.
            </p>
            <p className="text-lg text-[#F5F5F5] text-center leading-relaxed mt-4">
              TractionBee is built for <span className="text-cacao-gold">founders who need results without the overhead</span>.
            </p>
            <p className="text-cacao-muted text-center mt-6">
              We handle lead discovery, research, and email writing so you can focus on what matters: building your product and closing deals.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="border-t border-cacao-dark-tertiary bg-gradient-to-b from-cacao-dark-secondary to-cacao-dark py-20">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#F5F5F5]">
            Ready to try the founder-first approach?
          </h2>
          <p className="mt-4 text-lg text-cacao-muted">
            Start your free trial and see how TractionBee compares — no credit card required.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={ROUTES.SIGNUP}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-lg bg-cacao-gold px-8 py-3.5 text-base font-semibold text-cacao-dark shadow-lg shadow-cacao-gold/25 hover:bg-cacao-gold-hover hover:shadow-cacao-gold/40 transition-all"
            >
              Start free trial
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
          <p className="mt-4 text-sm text-cacao-text-tertiary">
            14 days free • No credit card • Cancel anytime
          </p>
        </div>
      </section>
    </div>
  );
}
